import { useState, useRef, useCallback, useEffect } from "react";
import type { FundComplianceFileRef } from "@/lib/fund-compliance/api";

interface ChatInputProps {
  onSend: (content: string) => void;
  onAttachFile?: (file: File) => void;
  onClearAttachment?: (index: number) => void;
  attachments?: FundComplianceFileRef[];
  disabled?: boolean;
  sendDisabled?: boolean;
  placeholder?: string;
  preserveValueOnSend?: boolean;
}

const MAX_ROWS = 5;
const LINE_HEIGHT = 20;
const PADDING = 20;

export function ChatInput({
  onSend,
  onAttachFile,
  onClearAttachment,
  attachments = [],
  disabled,
  sendDisabled,
  placeholder,
  preserveValueOnSend = false,
}: ChatInputProps) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const maxHeight = LINE_HEIGHT * MAX_ROWS + PADDING;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [value]);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if ((!trimmed && attachments.length === 0) || disabled || sendDisabled) return;
    onSend(trimmed);
    if (!preserveValueOnSend) {
      setValue("");
    }
  }, [attachments.length, value, disabled, onSend, preserveValueOnSend, sendDisabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <div className="px-4 py-3 sm:px-6 sm:py-4">
      <div className="max-w-3xl mx-auto border border-[var(--color-border)] bg-[var(--color-surface)] rounded-2xl px-3 py-2">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 border-b border-[var(--color-border)] pb-2 mb-2">
            {attachments.map((attachment, index) => (
              <div
                key={`${attachment.storageId}-${index}`}
                className="flex items-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-border)]/25 px-2.5 py-1.5 text-xs text-[var(--color-fg)]"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--color-fg-tertiary)]">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                  <path d="M14 2v6h6" />
                </svg>
                <span className="max-w-[220px] truncate">{attachment.filename}</span>
                {attachment.isUploading && (
                  <span className="flex items-center gap-1 text-[10px] text-[var(--color-fg-tertiary)]" style={{ fontFamily: "var(--font-mono)" }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Uploading
                  </span>
                )}
                {attachment.filesize && attachment.filesize !== "Uploaded file" && (
                  <span className="text-[10px] text-[var(--color-fg-tertiary)]" style={{ fontFamily: "var(--font-mono)" }}>
                    {attachment.filesize}
                  </span>
                )}
                {onClearAttachment && (
                  <button
                    type="button"
                    onClick={() => onClearAttachment(index)}
                    className="flex h-5 w-5 items-center justify-center rounded-full text-[var(--color-fg-tertiary)] hover:bg-[var(--color-border)]"
                    aria-label="Remove attachment"
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
        {/* Attachment button (placeholder for future use) */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg-secondary)] disabled:opacity-40 transition-colors cursor-pointer"
          aria-label="Attach file"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          className="hidden"
          disabled={disabled}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && onAttachFile) {
              onAttachFile(file);
            }
            e.currentTarget.value = "";
          }}
        />

        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? "Ask a follow-up about this review..."}
          disabled={disabled}
          rows={1}
          className="flex-1 resize-none bg-transparent text-sm text-[var(--color-fg)] placeholder:text-[var(--color-fg-tertiary)] px-1 py-1 outline-none"
          style={{ lineHeight: `${LINE_HEIGHT}px`, fontFamily: "var(--font-display)" }}
        />

        <button
          type="button"
          onClick={handleSend}
          disabled={disabled || sendDisabled || attachments.some((attachment) => attachment.isUploading) || (!value.trim() && attachments.length === 0)}
          className="shrink-0 h-8 w-8 flex items-center justify-center rounded-full bg-[var(--color-accent)] text-white disabled:opacity-30 hover:opacity-90 transition-opacity cursor-pointer"
          aria-label="Send message"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="19" x2="12" y2="5" />
            <polyline points="5 12 12 5 19 12" />
          </svg>
        </button>
        </div>
      </div>
    </div>
  );
}
